/**
 * Care-location confirmation for treatment journeys.
 *
 * Selecting a state updates local UI state. The explicit confirmation action
 * must await the server transition, because the backend owns eligibility and
 * product availability for that care location.
 */
import { TREATMENT_CHECKOUT_ERROR_COPY } from "./treatmentUiConstants";

export const CARE_LOCATION_STATUS = Object.freeze({
  idle: "idle",
  synchronizing: "synchronizing",
  advanced: "advanced",
  failed: "failed",
});

/** Retained compatibility export; Phase II state selection never auto-advances. */
export const AUTO_ADVANCE_QUESTION_TYPES = Object.freeze(
  new Set()
);

export const autoAdvancesOnSelection = (question) =>
  AUTO_ADVANCE_QUESTION_TYPES.has(question?.question_type);

/**
 * Handle a care-state selection.
 *
 * `synchronize(state)` performs the awaited runtime transition. `onUpdate`
 * receives each status change so the UI can show the selection immediately
 * (step 2) while remaining disabled until the server answers (step 3).
 *
 * Returns `{ status, error }`. On failure the caller stays on the state screen
 * with an actionable message; it must not advance (LOC-03).
 */
export const selectCareLocation = async (
  state,
  { synchronize, onUpdate, inFlight = false } = {}
) => {
  // LOC-04: a repeated click while synchronizing must not produce a second
  // transition or a second revision increment.
  if (inFlight) {
    return { status: CARE_LOCATION_STATUS.synchronizing, error: null };
  }

  onUpdate?.({ status: CARE_LOCATION_STATUS.synchronizing, value: state });
  try {
    await synchronize(state);
    onUpdate?.({ status: CARE_LOCATION_STATUS.advanced, value: state });
    return { status: CARE_LOCATION_STATUS.advanced, error: null, details: null };
  } catch (error) {
    const responseData = error?.response?.data || {};
    const code = error?.code || responseData.error || null;
    onUpdate?.({ status: CARE_LOCATION_STATUS.failed, value: state });
    return {
      status: CARE_LOCATION_STATUS.failed,
      error: code,
      details: responseData.details || error?.details || null,
      message: responseData.message || error?.message || null,
    };
  }
};

/** Patient-safe availability copy, keyed by the backend's intake error code. */
export const CARE_LOCATION_MESSAGE = Object.freeze({
  care_location_invalid: "Please choose the state where you will receive care.",
  care_location_unavailable:
    "This treatment is not available in the state you selected.",
  product_unavailable_for_care_location:
    TREATMENT_CHECKOUT_ERROR_COPY.product_unavailable_for_care_location,
});

export const careLocationMessage = (
  code,
  details = null,
  serverMessage = null,
  context = null,
) => {
  const treatmentName = String(context?.treatmentName || "This treatment").trim();
  const state = String(context?.state || details?.service_state || "").trim().toUpperCase();
  if (code === "care_location_unavailable" && state) {
    return `Unfortunately ${treatmentName} isn’t available in ${state} yet. We’re expanding state by state.`;
  }
  const productLabel = String(
    details?.product_label || details?.label || "",
  ).trim();
  if (code === "product_unavailable_for_care_location" && productLabel) {
    const action = details?.corrective_action?.label;
    const message = `${productLabel} is not available in your care location.`;
    return action ? `${message} ${action}.` : message;
  }
  const base = CARE_LOCATION_MESSAGE[code] ||
    TREATMENT_CHECKOUT_ERROR_COPY[code] ||
    (typeof serverMessage === "string" && serverMessage.trim()) ||
    "We could not confirm care in that state. Please try again.";
  const action = details?.corrective_action?.label;
  return action ? `${base} ${action}.` : base;
};
